import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, UserCheck, CheckCircle, Clock, AlertTriangle, Loader, ChevronDown } from "lucide-react";
import { groundOpsApi } from "@/api";

const STATUS_COLOR = {
  not_started: "text-slate-500 border-slate-600/30 bg-slate-800/20",
  in_progress:  "text-blue-400 border-blue-500/30 bg-blue-500/10",
  completed:    "text-green-400 border-green-500/30 bg-green-500/10",
  delayed:      "text-orange-400 border-orange-500/30 bg-orange-500/10",
  blocked:      "text-red-400 border-red-500/30 bg-red-500/10",
};
const STATUS_ICON = { in_progress: Clock, completed: CheckCircle, delayed: AlertTriangle, blocked: AlertTriangle };

export default function RampFlightTasks({ user }) {
  const { flightId } = useParams();
  const navigate      = useNavigate();

  const [flight, setFlight]    = useState(null);
  const [allStaff, setAllStaff] = useState([]);
  const [loading, setLoading]  = useState(true);
  const [openPicker, setOpenPicker] = useState(null);   // taskId whose picker is open
  const [assigning, setAssigning]   = useState(null);

  function load() {
    setLoading(true);
    Promise.all([
      groundOpsApi.getFlight(flightId),
      groundOpsApi.getStaff(user.station),
    ]).then(([f, s]) => { setFlight(f); setAllStaff(s); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }
  useEffect(load, [flightId, user.station]);

  async function assign(taskId, staffName) {
    setAssigning(taskId);
    try {
      await groundOpsApi.assignTask(taskId, staffName);
      // update local state
      setFlight(prev => ({
        ...prev,
        tasks: prev.tasks.map(t => t.id === taskId ? { ...t, assigned_to: staffName } : t),
      }));
      setOpenPicker(null);
    } finally { setAssigning(null); }
  }

  if (loading) return <div className="p-6 text-center text-slate-500 text-sm animate-pulse">Loading flight tasks...</div>;
  if (!flight) return (
    <div className="p-6 text-center">
      <div className="text-slate-400 mb-4">Flight not found</div>
      <button onClick={() => navigate(-1)} className="text-orange-400 text-sm border border-orange-500/30 rounded-xl px-4 py-2">Back</button>
    </div>
  );

  const tasks = flight.tasks || [];
  const done  = tasks.filter(t => t.status === "completed").length;

  return (
    <div className="p-4 max-w-sm mx-auto">
      <button onClick={() => navigate(-1)} className="flex items-center gap-2 text-slate-500 text-sm mb-4">
        <ArrowLeft size={16} /> Back
      </button>

      {/* Flight header */}
      <div className="bg-[#0c1929] rounded-2xl p-4 mb-4 border border-orange-500/15">
        <div className="flex items-center justify-between mb-1">
          <span className="text-lg font-black text-white">{flight.flight_number}</span>
          <span className={`text-[9px] font-bold uppercase px-2 py-1 rounded ${
            flight.risk_level === "critical" ? "bg-red-500/20 text-red-400" :
            flight.risk_level === "high"     ? "bg-orange-500/20 text-orange-400" :
            "bg-green-500/15 text-green-400"}`}>
            {flight.risk_level}
          </span>
        </div>
        <div className="text-[11px] text-slate-500">{flight.origin} → {flight.destination} · Stand {flight.stand}</div>
        <div className="flex items-center gap-2 mt-2">
          <div className="flex-1 bg-slate-700/30 rounded-full h-1.5">
            <div className="h-full bg-orange-500 rounded-full transition-all" style={{ width: `${tasks.length ? (done/tasks.length)*100 : 0}%` }} />
          </div>
          <span className="text-[10px] text-slate-500">{done}/{tasks.length}</span>
        </div>
      </div>

      {/* All tasks */}
      <div className="text-[9px] text-slate-600 uppercase tracking-widest font-bold mb-2 px-1">
        All Tasks — {tasks.length} total
      </div>
      <div className="space-y-2">
        {tasks.map(task => {
          const pickerOpen = openPicker === task.id;
          const StatusIcon = STATUS_ICON[task.status] || Clock;
          const candidates = allStaff.filter(s => {
            const roleKey = s.role;          // e.g. "ramp_agent"
            const taskRole = (task.role || "").toLowerCase().replace(/ /g, "_");
            return roleKey === taskRole || roleKey === "supervisor";
          });

          return (
            <div key={task.id} className={`rounded-2xl border overflow-hidden transition-all ${STATUS_COLOR[task.status] || STATUS_COLOR.not_started}`}>
              {/* Task row */}
              <div className="flex items-start gap-3 px-4 py-3">
                <StatusIcon size={14} className="flex-shrink-0 mt-0.5" />
                <div className="flex-1 min-w-0">
                  <div className="font-bold text-sm text-white">{task.name}</div>
                  <div className="text-[10px] text-slate-500 mt-0.5">
                    {new Date(task.planned_time).toLocaleTimeString("en-IN", {hour:"2-digit", minute:"2-digit", hour12:false, timeZone:"Asia/Kolkata"})}
                    {task.vendor && task.vendor !== "—" && <span> · {task.vendor}</span>}
                  </div>
                </div>
                <span className={`text-[8px] font-bold uppercase px-2 py-1 rounded-lg ${STATUS_COLOR[task.status]}`}>
                  {task.status.replace("_", " ")}
                </span>
              </div>

              {/* Assignee + reassign */}
              <div className="px-4 pb-3 flex items-center justify-between">
                <div>
                  <div className="text-[9px] text-slate-600 uppercase tracking-wider">Assigned</div>
                  <div className="text-[11px] font-semibold text-white mt-0.5">{task.assigned_to || "Unassigned"}</div>
                </div>
                {candidates.length > 0 && (
                  <button
                    onClick={() => setOpenPicker(pickerOpen ? null : task.id)}
                    className="flex items-center gap-1 text-[10px] font-bold text-orange-400 border border-orange-500/30 bg-orange-500/10 px-2.5 py-1.5 rounded-xl hover:bg-orange-500/20 transition"
                    data-testid={`reassign-task-${task.id?.slice(0, 6)}`}
                  >
                    <UserCheck size={11} />
                    Reassign
                    <ChevronDown size={9} className={`transition-transform ${pickerOpen ? "rotate-180" : ""}`} />
                  </button>
                )}
              </div>

              {/* Staff picker dropdown */}
              {pickerOpen && (
                <div className="px-4 pb-3 border-t border-white/5 bg-black/20 space-y-1.5">
                  <div className="text-[8px] text-slate-600 uppercase tracking-widest pt-2 mb-1">Pick staff to assign</div>
                  {candidates.map(s => (
                    <button
                      key={s.id}
                      onClick={() => assign(task.id, s.name)}
                      disabled={assigning === task.id}
                      className={`w-full flex items-center justify-between px-3 py-2 rounded-xl border text-sm transition-all disabled:opacity-50
                        ${s.name === task.assigned_to
                          ? "bg-orange-500/10 border-orange-500/30 text-orange-400"
                          : "bg-[#0c1929] border-slate-600/30 text-slate-300 hover:border-orange-500/30 hover:text-white"}`}
                      data-testid={`pick-staff-${s.id}`}
                    >
                      <div className="text-left">
                        <div className="font-semibold text-[12px]">{s.name}</div>
                        <div className="text-[9px] text-slate-500 capitalize">{s.role.replace(/_/g," ")}</div>
                      </div>
                      {assigning === task.id && <Loader size={12} className="animate-spin text-slate-500" />}
                      {s.name === task.assigned_to && <span className="text-[8px] text-orange-400 font-black uppercase">Current</span>}
                    </button>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
